import { Button } from '@design-system';
import { formatPrice } from '@utils/prices';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { Image } from '../../../components/Image';
import { pathFor } from '../../../helpers/links';
import { GeneratedMoneySet } from '../../../types/generated';
import { CurrencyCode } from '../../../types/Locations';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';

interface Props {
  backgroundImage: StoryblokImage | null;

  isAmlRequired: boolean;
  onClose: () => void;
  productTitle: string;
  offer?: GeneratedMoneySet | null;
}

export const ConfirmationDialog = ({
  backgroundImage,
  isAmlRequired,
  offer,
  onClose,
  productTitle,
}: Props) => {
  const router = useRouter();
  const { t } = useTranslation(['product']);

  const [emitter] = useAnalyticsEmitter();

  const handleClose = () => {
    emitter('close', 'clicked');
    onClose();
  };

  return (
    <div className="relative flex w-full max-w-md flex-col overflow-hidden rounded-t-lg bg-white md:rounded-lg">
      <div className="absolute right-6 top-6 flex items-center justify-center">
        <Button
          hideLabel
          icon="ic_close"
          label="Close"
          onClick={handleClose}
          size="sm"
          variant="secondary"
        />
      </div>

      <div className="relative flex aspect-[3/2] w-full flex-col items-center justify-center">
        {backgroundImage ? (
          <Image
            alt={backgroundImage?.alt ?? ''}
            size={{
              sm: { span: 3, ratio: '3/2' },
              md: { span: 4, ratio: '3/2' },
              lg: { span: 12, ratio: '3/2' },
            }}
            src={backgroundImage.filename}
          />
        ) : null}
      </div>

      <div className="p-6">
        <div className="mx-auto flex w-full max-w-md flex-col items-center text-center">
          <p className="text-md font-bold">
            {t('drawers.offer.confirmationDialog.title')}
          </p>
          {offer && (
            <p className="mb-6 mt-3 text-neutral-4">
              {t(
                isAmlRequired
                  ? 'drawers.offer.confirmationDialog.descriptionAML'
                  : 'drawers.offer.confirmationDialog.description',
                {
                  productTitle,
                  offer: formatPrice(
                    Number(offer.amount),
                    offer.currencyCode as CurrencyCode,
                  ),
                },
              )}
            </p>
          )}
          <Button
            fullWidth
            label={t(
              isAmlRequired
                ? 'drawers.offer.confirmationDialog.ctaAML'
                : 'drawers.offer.confirmationDialog.cta',
            )}
            onClick={() => {
              if (isAmlRequired) {
                emitter('AML', 'clicked');
                router.push('/aml');
                handleClose();
              } else {
                emitter('discover', 'clicked');
                router.push(pathFor({ page: 'WORKS_INDEX' }));
                handleClose();
              }
            }}
            variant="primary"
          />
        </div>
      </div>
    </div>
  );
};
